import { createFileRoute, Outlet } from "@tanstack/react-router";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { AppHeader } from "@bfc-software/bfc-web-ui/ui/shared/app-header";
import { LocationSelector } from "../../../-components/location-selector";
import { WarehouseGate } from "../../../-components/warehouse-gate";
import { OutboundSidebar } from "./-components/outbound-sidebar";

/**
 * Outbound's shell: the header, the navigation rail, and the warehouse the
 * screens below run against.
 */
export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/outbound/whs/$location/_layout",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const params = Route.useParams();
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.outbound,
  });

  return (
    <>
      <AppHeader actions={<LocationSelector className="mr-8" />} />
      {/*
        `min-h-0` on both this row and the content column below is what makes
        the content scroll rather than the page: the org layout's
        `AppContainer` is a column flex that fills the viewport, so without it
        a tall table pushes this row past the window and takes the header off
        screen with it.
      */}
      <div className="flex flex-1 min-h-0 border-t border-outline-variant">
        <OutboundSidebar />
        {/*
          Held back until the session matches the warehouse in the URL, so a
          screen never loads against the one being left - or against a warehouse
          the organisation does not have (see `WarehouseGate`). The rail stays
          mounted through it: it is scoped by the URL rather than the session, so
          blanking it would make a warehouse switch look like a navigation.

          `relative` makes this scroller the containing block for absolutely
          positioned descendants. Without it, react-aria's `VisuallyHidden`
          inputs (every Checkbox and chip radio) resolve against the initial
          containing block, escape this `overflow-auto`, and stretch the
          document to the last row's checkbox - a second, page-level scroll
          past the end of the app.
        */}
        <div className="relative flex flex-1 flex-col min-w-0 min-h-0 overflow-auto bg-background">
          <WarehouseGate
            lang={params.lang}
            org={params.org}
            location={params.location}
            fallbackTo="/$lang/orgs/$org/outbound"
            loading={
              <p className="p-32 text-on-surface-variant text-size-16">
                {t("shell.loadingWarehouse")}
              </p>
            }
          >
            <Outlet />
          </WarehouseGate>
        </div>
      </div>
    </>
  );
}
